import { PdfImportCountry } from 'components/pdf-import-country';
import { emptyPdfImportRequest, pdfImportReviewRequestDays, pdfImportLineKey, PDF_IMPORT_TAX_NOTE_PREFIX } from 'semshared/offer/pdf-import';
import { PdfImportBilling, PdfImportRequestDetails } from 'components/pdf-import-request';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, gql, useMutation, useQuery } from '@apollo/client';
import i18next from 'i18next';
import { tr } from 'helper/translate';
import { extractClientErrors } from 'helper/apollo';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Checkbox,
  FormControlLabel,
  Chip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  PdfImportContext,
  PdfImportHotelCandidate,
  PdfImportJobView,
  PdfImportGroupMember,
  PdfImportBookingConflict,
  PdfImportTarget,
  PdfImportJobListItem,
  PDF_IMPORT_MAX_GROUP_SIZE,
  PdfOfferImportLine,
  PdfOfferImportReview,
  PdfImportTaxShare,
  PdfImportIssue,
  PdfImportSection,
  formatPdfImportIssue,
  pdfImportReviewIssues,
  calculatePdfImportAmounts,
  changePdfImportPriceBasis,
  automaticPdfImportPriceList,
  emptyPdfImportCancellationRule,
  extendPdfImportEventRange,
  pdfImportResetDerivedAmounts,
  pdfImportMixedShares,
  pdfImportCents,
  pdfImportCancellationRuleText,
  pdfImportServiceCategories,
} from 'semshared/offer/pdf-import';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';
import { PdfPreview } from 'components/pdf-preview';
import { PdfImportEditor } from 'components/pdf-import-editor';
import { LabeledFrame } from 'components/LabeledFrame';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { formatDateTime, formatPrice } from 'semshared/utils/format';

export const PDF_IMPORT_ENABLED_QUERY = gql`
  query PdfOfferImportEnabled {
    pdfOfferImportEnabled
  }
`;
const JOB_FIELDS = gql`
  fragment PdfImportJobFields on PdfOfferImportJobOutput {
    id
    status
    filename
    revision
    error
    reviewJson
    candidatesJson
    rawJson
    validationJson
    offerId
    offerVersionId
    groupJson
    bookingConflictJson
    targetJson
  }
`;
const JOB = gql`
  query PdfOfferImportJob($id: String!) {
    job: pdfOfferImportJob(id: $id) {
      ...PdfImportJobFields
    }
  }
  ${JOB_FIELDS}
`;
const JOBS = gql`
  query PdfOfferImportJobs {
    jobs: pdfOfferImportJobs
  }
`;
const START = gql`
  mutation StartPdfOfferImport($filename: String!, $base64: String!, $groupId: String, $targetOfferId: Int) {
    id: startPdfOfferImport(filename: $filename, base64: $base64, groupId: $groupId, targetOfferId: $targetOfferId)
  }
`;
const TARGET = gql`
  query PdfOfferImportTarget($offerId: Int!) {
    target: pdfOfferImportTarget(offerId: $offerId)
  }
`;
const LEAVE_GROUP = gql`
  mutation LeavePdfOfferImportGroup($id: String!) {
    job: leavePdfOfferImportGroup(id: $id) {
      ...PdfImportJobFields
    }
  }
  ${JOB_FIELDS}
`;
const SAVE = gql`
  mutation SavePdfOfferImportReview($id: String!, $revision: Int!, $reviewJson: String!) {
    job: savePdfOfferImportReview(id: $id, revision: $revision, reviewJson: $reviewJson) {
      ...PdfImportJobFields
    }
  }
  ${JOB_FIELDS}
`;
const IMPORT = gql`
  mutation ImportReviewedPdfOffer($id: String!, $revision: Int!, $confirmTotalDifference: Boolean) {
    job: importReviewedPdfOffer(id: $id, revision: $revision, confirmTotalDifference: $confirmTotalDifference) {
      ...PdfImportJobFields
    }
  }
  ${JOB_FIELDS}
`;
const TAX_SUGGESTIONS = gql`
  mutation SuggestPdfOfferImportTaxes($id: String!, $revision: Int!, $reviewJson: String!) {
    result: suggestPdfOfferImportTaxes(id: $id, revision: $revision, reviewJson: $reviewJson)
  }
`;
const RETRY = gql`
  mutation RetryPdfOfferImport($id: String!) {
    job: retryPdfOfferImport(id: $id) {
      ...PdfImportJobFields
    }
  }
  ${JOB_FIELDS}
`;
const CONTEXT = gql`
  query PdfOfferImportContext($hotelId: Int!) {
    context: pdfOfferImportContext(hotelId: $hotelId)
  }
`;
const HOTELS = gql`
  query PdfOfferImportHotels($search: String!, $selectedId: Int, $offset: Int) {
    hotels: pdfOfferImportHotels(search: $search, selectedId: $selectedId, offset: $offset)
  }
`;
const CLIENTS = gql`
  query PdfOfferImportClients($search: String!) {
    clients: pdfOfferImportClients(search: $search)
  }
`;

const COMPANIES = gql`
  query PdfOfferImportCompanies($search: String!, $selectedId: Int) {
    companies: pdfOfferImportCompanies(search: $search, selectedId: $selectedId)
  }
`;

type TaxSuggestion = {
  lineIndex: number;
  lineKey?: string;
  taxRateId?: number | null;
  sku: string | null;
  status: 'ASSIGNED' | 'REVIEW';
  reason: string;
};
type HotelChoice = { id: number; name: string; businessCity: string };
type CompanyChoice = { id: number; name: string; isPremium: boolean };
type ClientChoice = PdfOfferImportReview['client'] & {
  id: number;
  billingCompany: string;
  billingFirstname: string;
  billingLastname: string;
  billingZip: string;
  billingCity: string;
  billingCountry: string;
};
const documentLabel = (type: PdfOfferImportReview['documentType']): string => tr(`pdf-import-document-${type}`);
const json = <T,>(value: string | null | undefined, fallback: T): T => (value ? (JSON.parse(value) as T) : fallback);
const number = (value: string): number | null => (value === '' ? null : Number(value));

const useDebounced = (value: string): string => {
  const [current, setCurrent] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setCurrent(value), 250);
    return () => clearTimeout(timer);
  }, [value]);
  return current;
};

const download = (name: string, value: unknown): void => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const readableError = (message: string): string => {
  if (/Ausgabelimit/.test(message)) return tr('pdf-import-error-too-large');
  if (/BDA|GPT|Azure|PDF_IMPORT_|HTTP|AccessDenied|GraphQL|Enum/.test(message)) return tr('pdf-import-error-unavailable');
  return message;
};
/** Server errors carry a code with a `graphqlerror-<code>` translation; other errors keep their message. */
const actionError = (err: unknown): string =>
  err instanceof ApolloError ? extractClientErrors(err).join('\n') : err instanceof Error ? err.message : tr('pdf-import-error-action');

const statusLabel = (status: string): string => (i18next.exists(`pdf-import-status-${status}`) ? tr(`pdf-import-status-${status}`) : status);

type ReviewTab = 'overview' | 'customer' | 'event' | 'services' | 'terms';
const reviewTabs: ReviewTab[] = ['overview', 'customer', 'event', 'services', 'terms'];
const sectionLabel = (section: ReviewTab | PdfImportSection): string => tr(`pdf-import-section-${section}`);
/** Open check in the reader's language; frequent ones get a friendlier wording than the shared validation text. */
const readableIssue = (issue: PdfImportIssue, language: string): string => {
  if (!['hotelMissing', 'email', 'country', 'taxMissing', 'priceListMissing'].includes(issue.key)) return formatPdfImportIssue(issue, language);
  const prefix = issue.line ? tr('pdf-import-item', { n: issue.line }) + ': ' : '';
  return prefix + tr(`pdf-import-issue-${issue.key}`);
};

const statusColors: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  REVIEW: 'info',
  IMPORTED: 'success',
  FAILED: 'error',
};
const isoDate = (value: string): string => (value ? value.split('-').reverse().join('.') : '');

type UploadState = { name: string; state: 'waiting' | 'uploading' | 'done' | 'error' };

/** Drop area for one or more PDFs; files dropped or selected together become one booking. */
const PdfImportDropzone = ({
  disabled,
  uploads,
  onFiles,
  versions = false,
}: {
  disabled: boolean;
  uploads: UploadState[];
  onFiles: (files: File[]) => void;
  /** Uploads for an existing booking become new versions instead of new bookings. */
  versions?: boolean;
}) => {
  const [dragging, setDragging] = useState(false);
  return (
    <Paper
      variant="outlined"
      onDragOver={event => {
        event.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={event => {
        event.preventDefault();
        setDragging(false);
        if (!disabled) onFiles(Array.from(event.dataTransfer.files));
      }}
      sx={{
        p: 3,
        borderRadius: 2,
        borderStyle: 'dashed',
        borderWidth: 2,
        borderColor: dragging ? 'primary.main' : 'divider',
        bgcolor: dragging ? 'action.hover' : 'background.paper',
        transition: 'border-color 120ms, background-color 120ms',
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }}>
        <Stack alignItems="center" spacing={1} sx={{ minWidth: 240 }}>
          <UploadFileIcon color={dragging ? 'primary' : 'action'} sx={{ fontSize: 48 }} />
          <Typography variant="subtitle1" textAlign="center">
            {tr('pdf-import-drop-here')}
          </Typography>
          <Button variant="contained" component="label" disabled={disabled}>
            {tr('pdf-import-choose-files')}
            <input
              type="file"
              accept="application/pdf,.pdf"
              multiple
              hidden
              onChange={event => {
                const files = Array.from(event.target.files || []);
                event.target.value = '';
                if (files.length) onFiles(files);
              }}
            />
          </Button>
        </Stack>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
        <Stack spacing={1.5} sx={{ flex: 1 }}>
          {['offer', 'confirmation', 'both']
            .map(key => [tr(`pdf-import-case-${key}-title`), tr(`pdf-import-case-${key}-${versions ? 'version' : 'text'}`)])
            .map(([title, text]) => (
              <Typography key={title} variant="body2">
                <strong>{title}</strong> {text}
              </Typography>
            ))}
          <Typography variant="caption" color="text.secondary">
            {tr('pdf-import-drop-limits', { max: PDF_IMPORT_MAX_GROUP_SIZE })}
          </Typography>
        </Stack>
      </Stack>
      {uploads.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          {uploads.map(upload => (
            <Stack key={upload.name} direction="row" spacing={1} alignItems="center">
              {upload.state === 'done' ? (
                <CheckCircleOutlineIcon color="success" fontSize="small" />
              ) : upload.state === 'error' ? (
                <ErrorOutlineIcon color="error" fontSize="small" />
              ) : upload.state === 'uploading' ? (
                <CircularProgress size={16} />
              ) : (
                <Box sx={{ width: 20 }} />
              )}
              <Typography variant="body2" color={upload.state === 'waiting' ? 'text.secondary' : 'text.primary'}>
                {upload.name}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

const isOpenJob = (item: PdfImportJobListItem) => item.status !== 'IMPORTED';
type JobFilter = 'open' | 'imported' | 'all';

/** Recent uploads; documents of one booking are listed together under a booking row. */
const PdfImportJobTable = ({ items }: { items: PdfImportJobListItem[] }) => {
  const navigate = useNavigate();
  // A booking is open while any of its documents is not imported; filters never split a booking.
  const bookings = useMemo(() => {
    const result: PdfImportJobListItem[][] = [];
    for (const item of items) {
      const last = result[result.length - 1];
      if (item.groupId && last?.[0].groupId === item.groupId) last.push(item);
      else result.push([item]);
    }
    return result;
  }, [items]);
  const counts = {
    open: bookings.filter(members => members.some(isOpenJob)).length,
    imported: bookings.filter(members => !members.some(isOpenJob)).length,
    all: bookings.length,
  };
  const [filter, setFilter] = useState<JobFilter | null>(null);
  const active: JobFilter = filter || (counts.open ? 'open' : 'all');
  const visible = bookings.filter(members => active === 'all' || (active === 'open') === members.some(isOpenJob));
  const columns = 7;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
        <Typography variant="h6">{tr('pdf-import-uploads')}</Typography>
        <ToggleButtonGroup size="small" exclusive value={active} onChange={(_event, value: JobFilter | null) => value && setFilter(value)}>
          <ToggleButton value="open">{tr('pdf-import-filter-open', { count: counts.open })}</ToggleButton>
          <ToggleButton value="imported">{tr('pdf-import-filter-imported', { count: counts.imported })}</ToggleButton>
          <ToggleButton value="all">{tr('pdf-import-filter-all', { count: counts.all })}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {!visible.length ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {tr(!items.length ? 'pdf-import-empty-none' : active === 'open' ? 'pdf-import-empty-open' : 'pdf-import-empty-imported')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tr('pdf-import-col-document')}</TableCell>
                <TableCell>{tr('pdf-import-col-hotel')}</TableCell>
                <TableCell>{tr('pdf-import-col-customer')}</TableCell>
                <TableCell>{tr('pdf-import-col-period')}</TableCell>
                <TableCell align="right">{tr('pdf-import-col-total')}</TableCell>
                <TableCell>{tr('pdf-import-col-status')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map(members => {
                const grouped = members.length > 1;
                return (
                  <React.Fragment key={members[0].groupId || members[0].id}>
                    {grouped && (
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell colSpan={columns} sx={{ py: 0.75 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {tr('pdf-import-booking-documents', { count: members.length })}
                            <Typography component="span" variant="body2" color="text.secondary">
                              {' · '}
                              {members.map(item => (item.documentType ? documentLabel(item.documentType) : '…')).join(' → ')}
                              {members.some(isOpenJob)
                                ? members.every(item => item.status === 'REVIEW')
                                  ? ` · ${tr('pdf-import-booking-all-ready')}`
                                  : ''
                                : ` · ${tr('pdf-import-booking-created')}`}
                            </Typography>
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {members.map(item => (
                      <TableRow key={item.id} hover onClick={() => navigate(`/offers/pdf-import/${item.id}`)} sx={{ cursor: 'pointer' }}>
                        <TableCell sx={{ pl: grouped ? 4 : 2, minWidth: 200, maxWidth: 320, wordBreak: 'break-word' }}>
                          <Typography variant="body2" fontWeight={500}>
                            {item.documentType ? documentLabel(item.documentType) : tr('pdf-import-col-document')}
                            {grouped ? ` · ${tr('pdf-import-version', { n: item.groupIndex + 1 })}` : ''}
                            {item.targetRefCode
                              ? ` · ${tr('pdf-import-list-target', { refCode: item.targetRefCode })}`
                              : item.bookingNumber
                                ? ` · ${item.bookingNumber}`
                                : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.filename}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.hotel || '–'}</TableCell>
                        <TableCell>{item.customer || '–'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {item.startDate
                            ? `${isoDate(item.startDate)}${item.endDate && item.endDate !== item.startDate ? ` – ${isoDate(item.endDate)}` : ''}`
                            : '–'}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {item.totalGross !== null ? formatPrice(item.totalGross) : '–'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={statusColors[item.status] || 'default'}
                            icon={['QUEUED', 'PROCESSING', 'IMPORTING'].includes(item.status) ? <CircularProgress size={12} /> : undefined}
                            label={statusLabel(item.status)}
                          />
                          {item.bookingConflict && (
                            <Chip
                              size="small"
                              color="error"
                              icon={<ErrorOutlineIcon />}
                              label={tr('pdf-import-booking-taken-chip')}
                              sx={{ mt: 0.5, display: 'flex', width: 'fit-content', fontWeight: 600 }}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, whiteSpace: 'nowrap' }}>
                            {formatDateTime(new Date(item.updatedAt))}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" onClick={event => event.stopPropagation()}>
                          {item.offerId ? (
                            <Button size="small" component={Link} to={`/offers/${item.offerId}/${item.offerVersionId}`}>
                              {tr('pdf-import-open')}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant={item.status === 'REVIEW' ? 'outlined' : 'text'}
                              component={Link}
                              to={`/offers/pdf-import/${item.id}`}
                            >
                              {tr(item.status === 'REVIEW' ? 'pdf-import-review' : 'pdf-import-details')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

export default function PdfOfferImportPage() {
  const { importId } = useParams();
  // Opened from a booking: uploads become new versions of that booking after the review.
  const [searchParams] = useSearchParams();
  const targetOfferId = Number(searchParams.get('offerId')) || null;
  const navigate = useNavigate();
  const [review, setReview] = useState<PdfOfferImportReview | null>(null);
  const [dirty, setDirty] = useState(false);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [hotelSearch, setHotelSearch] = useState('');
  const hotelPaging = useRef({ key: '', offset: 100, more: true, busy: false });
  const [companySearch, setCompanySearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [totalDifferenceConfirmed, setTotalDifferenceConfirmed] = useState(false);
  const [tab, setTab] = useState<ReviewTab>('overview');
  const [showRaw, setShowRaw] = useState(false);
  const [taxHints, setTaxHints] = useState<TaxSuggestion[] | null>(null);
  const taxHotel = useRef('');
  const currentReview = useRef(review);
  currentReview.current = review;
  const { data: enabledData, loading: enabledLoading } = useQuery<{ pdfOfferImportEnabled: boolean }>(PDF_IMPORT_ENABLED_QUERY);
  const enabled = !!enabledData?.pdfOfferImportEnabled;
  const {
    data: jobsData,
    refetch: reloadJobs,
    startPolling: startJobsPolling,
    stopPolling: stopJobsPolling,
  } = useQuery<{ jobs: string }>(JOBS, { skip: !enabled, fetchPolicy: 'network-only' });
  const jobItems = json<PdfImportJobListItem[]>(jobsData?.jobs, []);
  const { data: targetData, error: targetError } = useQuery<{ target: string }>(TARGET, {
    variables: { offerId: targetOfferId || 0 },
    skip: !enabled || !targetOfferId || !!importId,
    fetchPolicy: 'network-only',
  });
  const uploadTarget = json<PdfImportTarget | null>(targetData?.target, null);
  const jobsRunning = jobItems.some(item => ['QUEUED', 'PROCESSING', 'IMPORTING'].includes(item.status));
  useEffect(() => {
    if (!importId && jobsRunning) startJobsPolling(5000);
    else stopJobsPolling();
    return () => stopJobsPolling();
  }, [importId, jobsRunning, startJobsPolling, stopJobsPolling]);
  const {
    data,
    error: queryError,
    startPolling,
    stopPolling,
    refetch,
  } = useQuery<{ job: PdfImportJobView }>(JOB, {
    variables: { id: importId || '' },
    skip: !importId || !enabled,
    fetchPolicy: 'network-only',
  });
  const queriedJob = data?.job;
  const job = queriedJob?.id === importId ? queriedJob : undefined;
  const [start] = useMutation<{ id: string }>(START);
  const [save] = useMutation<{ job: PdfImportJobView }>(SAVE);
  const [importOffer] = useMutation<{ job: PdfImportJobView }>(IMPORT);
  const [suggestTaxes] = useMutation<{ result: string }>(TAX_SUGGESTIONS);
  const [retry] = useMutation<{ job: PdfImportJobView }>(RETRY);
  const [leaveGroup] = useMutation<{ job: PdfImportJobView }>(LEAVE_GROUP);
  const { data: contextData, error: contextError } = useQuery<{ context: string }>(CONTEXT, {
    variables: { hotelId: review?.hotelId || 0 },
    skip: !review?.hotelId || !enabled,
  });
  const context = useMemo(() => json<PdfImportContext | null>(contextData?.context, null), [contextData]);
  const companyTerm = useDebounced(companySearch);
  const clientTerm = useDebounced(clientSearch);
  const hotelTerm = useDebounced(hotelSearch);
  const { data: hotelData, fetchMore: moreHotels } = useQuery<{ hotels: string }>(HOTELS, {
    variables: { search: hotelTerm, selectedId: review?.hotelId, offset: 0 },
    skip: !review || !enabled,
  });
  // Without input, existing customers of the company named in the PDF are suggested.
  const { data: clientData } = useQuery<{ clients: string }>(CLIENTS, {
    variables: { search: clientTerm || review?.client.company || '' },
    skip: !review || !enabled || job?.status !== 'REVIEW',
  });
  const { data: companyData } = useQuery<{ companies: string }>(COMPANIES, {
    variables: { search: companyTerm, selectedId: review?.publicCompanyId },
    skip: !review || !enabled,
  });
  const candidates = useMemo(() => json<PdfImportHotelCandidate[]>(job?.candidatesJson, []), [job?.candidatesJson]);
  const preparation = useMemo(
    () =>
      json<{
        preparation?: {
          taxSuggestions?: TaxSuggestion[];
          printedTotals?: { gross: number | null; net: number | null };
          explanations: string[];
          calculatedFields: string[];
          issues: { field: string; reason: string; evidence: string }[];
        };
      }>(job?.rawJson, {}).preparation,
    [job?.rawJson],
  );
  const hotels = useMemo(() => {
    const options = json<HotelChoice[]>(hotelData?.hotels, []);
    const merged = new Map(candidates.map(c => [c.id, { id: c.id, name: c.name, businessCity: c.city }]));
    options.forEach(h => merged.set(h.id, h));
    return [...merged.values()];
  }, [hotelData, candidates]);
  const hotelPageKey = `${hotelTerm}:${review?.hotelId || ''}`;
  if (hotelPaging.current.key !== hotelPageKey) hotelPaging.current = { key: hotelPageKey, offset: 100, more: true, busy: false };
  const loadMoreHotels = async (): Promise<void> => {
    const page = hotelPaging.current;
    if (page.busy || !page.more || !hotelData) return;
    page.busy = true;
    try {
      const result = await moreHotels({
        variables: { offset: page.offset },
        updateQuery: (previous, { fetchMoreResult }) => ({
          hotels: JSON.stringify([
            ...new Map(
              [...json<HotelChoice[]>(previous.hotels, []), ...json<HotelChoice[]>(fetchMoreResult?.hotels, [])].map(h => [h.id, h]),
            ).values(),
          ]),
        }),
      });
      if (hotelPaging.current !== page) return;
      page.offset += 100;
      page.more = json<HotelChoice[]>(result.data?.hotels, []).length >= 100;
    } finally {
      page.busy = false;
    }
  };
  const storedReview = json<PdfOfferImportReview | null>(job?.reviewJson, null);
  const hintSource =
    taxHints ||
    (storedReview?.hotelId === review?.hotelId && storedReview?.priceListId === review?.priceListId ? preparation?.taxSuggestions : []) ||
    [];
  const visibleTaxHints = hintSource.flatMap(hint => {
    if (!review || !hint.lineKey) return [];
    const indexes = review.lineItems.flatMap((line, index) => (pdfImportLineKey(line) === hint.lineKey ? [index] : []));
    return indexes.length === 1 && (hint.status !== 'ASSIGNED' || review.lineItems[indexes[0]].taxRateId === hint.taxRateId)
      ? [{ ...hint, lineIndex: indexes[0] }]
      : [];
  });
  const companies = useMemo(() => json<CompanyChoice[]>(companyData?.companies, []), [companyData]);
  const clients = useMemo(() => json<ClientChoice[]>(clientData?.clients, []), [clientData]);
  const applyClient = (client: ClientChoice | null): void => {
    if (!client) return;
    edit(r => ({
      ...r,
      client: {
        ...r.client,
        company: client.company || r.client.company,
        firstname: client.firstname,
        lastname: client.lastname,
        email: client.email,
        phone: client.phone || '',
        address: client.address,
        zip: client.zip,
        city: client.city,
        country: client.country,
      },
      request: {
        ...(r.request || emptyPdfImportRequest()),
        billing:
          client.billingCompany || client.billingAddress
            ? {
                company: client.billingCompany,
                firstname: client.billingFirstname,
                lastname: client.billingLastname,
                address: client.billingAddress,
                zip: client.billingZip,
                city: client.billingCity,
                country: client.billingCountry,
              }
            : (r.request || emptyPdfImportRequest()).billing,
      },
    }));
  };
  // Printed PDF total vs. the offer that would be created; a deviation needs an explicit confirmation.
  const printedGross = pdfImportCents(preparation?.printedTotals?.gross ?? null);
  const offerGross = pdfImportCents(review?.totalGross ?? null) ?? 0;
  const totalDifference =
    review && printedGross !== null && Math.abs(printedGross - offerGross) > 1 ? { printed: printedGross / 100, offer: offerGross / 100 } : null;
  const language = i18next.language === 'en' ? 'en' : 'de';
  const target = json<PdfImportTarget | null>(job?.targetJson, null);
  const issues = review ? pdfImportReviewIssues(review, context?.hotel.id === review.hotelId ? context : null, { existingCustomer: !!target }) : [];
  const validation = issues.map(issue => readableIssue(issue, language));
  const openBySection = issues.reduce<Record<string, number>>((counts, { section }) => {
    counts[section] = (counts[section] || 0) + 1;
    return counts;
  }, {});
  // Documents uploaded together become versions of one booking; the current document's state is taken from the editor.
  const group = json<PdfImportGroupMember[]>(job?.groupJson, []).map(member =>
    member.id === importId && review
      ? { ...member, documentType: review.documentType, bookingNumber: review.bookingNumber, hotelId: review.hotelId, openChecks: validation.length }
      : member,
  );
  // The server checks the saved booking number; an edited, unsaved number is checked on saving.
  const storedConflict = json<PdfImportBookingConflict | null>(job?.bookingConflictJson, null);
  const bookingConflict = storedConflict && review && storedConflict.refCode === review.bookingNumber ? storedConflict : null;
  const bookingBlocked = !!bookingConflict && !bookingConflict.deleted;
  const otherMembers = group.filter(member => member.id !== importId);
  const groupBlocker = !group.length
    ? ''
    : otherMembers.some(member => member.status !== 'REVIEW')
      ? tr('pdf-import-group-pending')
      : otherMembers.some(member => member.openChecks > 0)
        ? tr('pdf-import-group-open-checks')
        : new Set(group.map(member => member.hotelId)).size > 1
          ? tr('pdf-import-group-hotel')
          : new Set(group.map(member => member.bookingNumber).filter(Boolean)).size > 1
            ? tr('pdf-import-group-numbers')
            : '';
  const differingDocuments = [
    ...(totalDifference ? [job?.filename || ''] : []),
    ...otherMembers.filter(member => member.totalDifference).map(member => member.filename),
  ];
  const importReservation = group.length
    ? group.some(member => member.documentType === 'BOOKING_CONFIRMATION')
    : review?.documentType === 'BOOKING_CONFIRMATION';

  useEffect(() => {
    setReview(null);
    setDirty(false);
    setRevision(0);
    setError('');
    setShowRaw(false);
    setTaxHints(null);
    setTotalDifferenceConfirmed(false);
    setTab('overview');
    taxHotel.current = '';
  }, [importId]);
  useEffect(() => {
    if (job?.status === 'QUEUED' || job?.status === 'PROCESSING' || job?.status === 'IMPORTING') startPolling(3000);
    // Keep the booking overview current while other documents of the upload are still being read.
    else if (json<PdfImportGroupMember[]>(job?.groupJson, []).some(member => ['QUEUED', 'PROCESSING'].includes(member.status))) startPolling(5000);
    else stopPolling();
    return () => stopPolling();
  }, [job?.status, job?.groupJson, startPolling, stopPolling]);
  useEffect(() => {
    if (!job || dirty) return;
    const next = json<PdfOfferImportReview | null>(job.reviewJson, null);
    setReview(next);
    setRevision(job.revision);
    if (next) {
      setCompanySearch(next.client.company);
    }
    // Keep in-progress edits when another query/mutation updates the cache.
  }, [job, dirty]);

  useEffect(() => {
    if (!review || !context || context.hotel.id !== review.hotelId || job?.status !== 'REVIEW') return;
    const priceListId = automaticPdfImportPriceList(context, review.priceListId);
    if (!priceListId || priceListId === review.priceListId) return;
    setReview(current => (current && current.hotelId === context.hotel.id ? { ...current, priceListId } : current));
    setDirty(true);
  }, [context, review?.hotelId, review?.priceListId, job?.status]);

  const edit = (change: (value: PdfOfferImportReview) => PdfOfferImportReview): void => {
    setReview(current => (current ? extendPdfImportEventRange(calculatePdfImportAmounts(change(current)).review) : current));
    setDirty(true);
  };
  const editLine = (index: number, changes: Partial<PdfOfferImportLine>): void =>
    edit(current => ({ ...current, lineItems: current.lineItems.map((line, i) => (i === index ? { ...line, ...changes } : line)) }));
  const action = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(actionError(err));
    } finally {
      setBusy(false);
    }
  };
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const uploadFiles = (files: File[]) =>
    action(async () => {
      const pdfs = files.filter(file => file.type === 'application/pdf' || /\.pdf$/i.test(file.name));
      if (!pdfs.length) throw new Error(tr('pdf-import-upload-only-pdf'));
      if (pdfs.length > PDF_IMPORT_MAX_GROUP_SIZE) throw new Error(tr('graphqlerror-PDF_IMPORT_GROUP_SIZE', { max: PDF_IMPORT_MAX_GROUP_SIZE }));
      if (pdfs.some(file => file.size > 20 * 1024 * 1024)) throw new Error(tr('graphqlerror-PDF_IMPORT_FILE_SIZE'));
      // PDFs selected together belong to one booking (e.g. the hotel's offer and its booking confirmation).
      const groupId = pdfs.length > 1 ? crypto.randomUUID() : null;
      const mark = (index: number, state: UploadState['state']) =>
        setUploads(current => current.map((upload, i) => (i === index ? { ...upload, state } : upload)));
      setUploads(pdfs.map(file => ({ name: file.name, state: 'waiting' })));
      const ids: string[] = [];
      for (const [index, file] of pdfs.entries()) {
        mark(index, 'uploading');
        try {
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(new Error(tr('pdf-import-upload-read-failed')));
            reader.onload = () => resolve(String(reader.result).split(',')[1]);
            reader.readAsDataURL(file);
          });
          const result = await start({ variables: { filename: file.name, base64, groupId, targetOfferId } });
          if (result.data) ids.push(result.data.id);
          mark(index, 'done');
        } catch (err) {
          mark(index, 'error');
          throw err;
        }
      }
      await reloadJobs();
      setUploads([]);
      if (ids.length) {
        setDirty(false);
        navigate(`/offers/pdf-import/${ids[0]}`);
      }
    });
  const saveReview = async (): Promise<number> => {
    if (!review || !importId) throw new Error(tr('pdf-import-open-pdf-first'));
    const result = await save({ variables: { id: importId, revision, reviewJson: JSON.stringify(review) } });
    if (result.data) {
      setReview(json(result.data.job.reviewJson, review));
      setRevision(result.data.job.revision);
      setDirty(false);
      return result.data.job.revision;
    }
    throw new Error(tr('pdf-import-save-failed'));
  };
  // Only calculated amounts are recalculated after a tax change; printed PDF amounts and gross totals stay visible.
  const amountsAfterTaxChange = (line: PdfOfferImportLine) => {
    const reset = pdfImportResetDerivedAmounts(line);
    return { totalNet: reset.totalNet, totalGross: reset.totalGross, taxAmount: reset.taxAmount };
  };
  const keptTotalGross = (current: PdfOfferImportReview) =>
    current.lineItems.every(line => line.priceBasis === 'GROSS') ? current.totalGross : null;
  const changeHotel = (hotel: HotelChoice | null): void => {
    const catalogMapped = new Set(visibleTaxHints.filter(hint => hint.status === 'ASSIGNED' && hint.sku).map(hint => hint.lineIndex));
    review?.lineItems.forEach((line, index) => {
      if (line.notes.includes(PDF_IMPORT_TAX_NOTE_PREFIX)) catalogMapped.add(index);
    });
    setTaxHints(null);
    taxHotel.current = '';
    edit(current => ({
      ...current,
      hotelId: hotel?.id || null,
      priceListId: null,
      totalNet: catalogMapped.size ? null : current.totalNet,
      totalGross: catalogMapped.size ? keptTotalGross(current) : current.totalGross,
      totalTax: catalogMapped.size ? null : current.totalTax,
      lineItems: current.lineItems.map((line, index) => ({
        ...line,
        taxRateId: null,
        taxShares: [],
        ...(catalogMapped.has(index)
          ? {
              taxPercent: null,
              ...amountsAfterTaxChange(line),
            }
          : {}),
      })),
    }));
  };
  const changeTax = (index: number, taxRateId: number | null): void => {
    const rate = context?.taxRates.find(item => item.id === taxRateId);
    edit(current => ({
      ...current,
      totalNet: null,
      totalGross: keptTotalGross(current),
      totalTax: null,
      lineItems: current.lineItems.map((line, i) =>
        i === index
          ? {
              ...line,
              taxRateId: rate?.id || null,
              taxShares: [],
              taxPercent: rate ? Math.round(rate.rate * 10000) / 100 : null,
              ...amountsAfterTaxChange(line),
            }
          : line,
      ),
    }));
  };
  const changeSplit = (indexes: number[], shares: PdfImportTaxShare[]): void =>
    edit(current => ({
      ...current,
      totalNet: null,
      totalGross: keptTotalGross(current),
      totalTax: null,
      lineItems: current.lineItems.map((line, index) =>
        indexes.includes(index)
          ? {
              ...line,
              taxRateId: null,
              taxPercent: null,
              taxShares: shares.map(share => ({ ...share })),
              ...amountsAfterTaxChange(line),
            }
          : line,
      ),
    }));
  const changeMixed = (index: number, taxRateIds: number[]): void => {
    const rates = context?.taxRates.filter(rate => taxRateIds.includes(rate.id)) || [];
    if (rates.length < 2) return;
    edit(current => ({
      ...current,
      totalNet: null,
      totalGross: keptTotalGross(current),
      totalTax: null,
      lineItems: current.lineItems.map((line, i) => {
        if (i !== index) return line;
        const base = { ...line, ...amountsAfterTaxChange(line), taxRateId: null, taxPercent: null };
        return { ...base, taxShares: pdfImportMixedShares(base, rates) };
      }),
    }));
  };
  const applyMappings = async (): Promise<void> => {
    if (!review || !importId || !context || context.hotel.id !== review.hotelId) return;
    const snapshot = JSON.stringify(review);
    const response = await suggestTaxes({ variables: { id: importId, revision, reviewJson: snapshot } });
    if (!response.data || JSON.stringify(currentReview.current) !== snapshot) return;
    const result = json<{ review: PdfOfferImportReview; suggestions: TaxSuggestion[] }>(response.data.result, { review, suggestions: [] });
    setTaxHints(result.suggestions);
    setReview(result.review);
    setDirty(true);
  };
  useEffect(() => {
    if (!review?.hotelId || !review.priceListId || !context || context.hotel.id !== review.hotelId || job?.status !== 'REVIEW') return;
    const key = `${importId}:${review.hotelId}:${review.priceListId}`;
    if (taxHotel.current === key) return;
    taxHotel.current = key;
    const stored = json<PdfOfferImportReview | null>(job.reviewJson, null);
    if (
      (preparation?.taxSuggestions && stored?.hotelId === review.hotelId && stored?.priceListId === review.priceListId) ||
      review.lineItems.every(line => line.taxRateId)
    )
      return;
    void action(applyMappings);
  }, [context, review?.hotelId, review?.priceListId, job?.status, importId]);

  // Without the feature flag nothing of the import is shown: a direct link leads back to the offer list.
  if (!enabled) return enabledLoading ? null : <Navigate to="/offers/lister" replace />;

  return (
    <Stack spacing={2}>
      <UnsavedChangesPrompt isDirty={dirty} />
      {!importId ? (
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5">{tr('pdf-import-title')}</Typography>
            <Typography color="text.secondary">{tr('pdf-import-subtitle')}</Typography>
          </Box>
          <Button component={Link} to="/offers/lister">
            {tr('pdf-import-to-offers')}
          </Button>
        </Stack>
      ) : (
        <Box>
          <Button component={Link} to="/offers/pdf-import" startIcon={<ArrowBackIcon />}>
            {tr('pdf-import-all-uploads')}
          </Button>
        </Box>
      )}
      {(error || queryError || contextError) && (
        <Alert severity="error">{readableError(error || queryError?.message || contextError?.message || '')}</Alert>
      )}
      {targetError && !importId && <Alert severity="error">{actionError(targetError)}</Alert>}
      {!importId && (
        <>
          {targetOfferId && uploadTarget && (
            <Alert
              severity={uploadTarget.closed ? 'error' : 'info'}
              action={
                <Button
                  color="inherit"
                  component={Link}
                  to={`/offers/${uploadTarget.offerId}${uploadTarget.latestVersionId ? `/${uploadTarget.latestVersionId}` : ''}`}
                >
                  {tr('pdf-import-target-open')}
                </Button>
              }
            >
              <AlertTitle>{tr('pdf-import-target-title', { refCode: uploadTarget.refCode })}</AlertTitle>
              {[uploadTarget.hotel, uploadTarget.customer].filter(Boolean).join(' · ')}.{' '}
              {tr(uploadTarget.closed ? 'pdf-import-target-closed' : 'pdf-import-target-upload-text')}
            </Alert>
          )}
          <PdfImportDropzone
            versions={!!targetOfferId}
            disabled={!enabled || busy || (!!targetOfferId && (!uploadTarget || uploadTarget.closed))}
            uploads={uploads}
            onFiles={files => void uploadFiles(files)}
          />
          <PdfImportJobTable items={targetOfferId ? jobItems.filter(item => item.targetOfferId === targetOfferId) : jobItems} />
        </>
      )}
      {importId && (
        <Stepper activeStep={job?.status === 'IMPORTED' ? 2 : 1} alternativeLabel sx={{ py: 1 }}>
          {[
            'pdf-import-step-upload',
            'pdf-import-step-review',
            target ? 'pdf-import-step-version' : group.length ? 'pdf-import-step-booking' : 'pdf-import-step-offer',
          ]
            .map(key => tr(key))
            .map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
        </Stepper>
      )}
      {job && (
        <>
          <Typography variant="h6">
            {job.filename} — {statusLabel(job.status)}
          </Typography>
          {['QUEUED', 'PROCESSING', 'IMPORTING'].includes(job.status) && (
            <Alert severity="info" icon={<CircularProgress size={20} />}>
              {tr('pdf-import-processing')}
            </Alert>
          )}
          {job.status === 'FAILED' && (
            <Alert
              severity="error"
              action={
                <Button
                  disabled={busy}
                  onClick={() =>
                    void action(async () => {
                      await retry({ variables: { id: importId } });
                      await refetch();
                    })
                  }
                >
                  Erneut versuchen
                </Button>
              }
            >
              {readableError(job.error || tr('pdf-import-failed-default'))} {tr('pdf-import-failed-hint')}
            </Alert>
          )}
          {job.status === 'IMPORTED' && (
            <Alert
              severity="success"
              action={
                <Button component={Link} to={`/offers/${job.offerId}/${group[group.length - 1]?.offerVersionId || job.offerVersionId}`}>
                  {tr(group.length || target ? 'pdf-import-open-booking' : 'pdf-import-open-offer')}
                </Button>
              }
            >
              {target
                ? tr('pdf-import-success-version', { refCode: target.refCode, count: Math.max(group.length, 1) })
                : group.length
                  ? tr('pdf-import-success-booking', {
                      count: group.length,
                      documents: group.map(member => documentLabel(member.documentType || 'UNKNOWN')).join(' → '),
                    })
                  : review?.documentType === 'BOOKING_CONFIRMATION'
                    ? tr('pdf-import-success-reservation')
                    : tr('pdf-import-success-offer')}
            </Alert>
          )}
          {target && job.status !== 'IMPORTED' && (
            <Alert
              severity={target.closed ? 'error' : 'info'}
              action={
                <Button
                  color="inherit"
                  component={Link}
                  to={`/offers/${target.offerId}${target.latestVersionId ? `/${target.latestVersionId}` : ''}`}
                >
                  {tr('pdf-import-target-open')}
                </Button>
              }
            >
              <AlertTitle>{tr('pdf-import-target-title', { refCode: target.refCode })}</AlertTitle>
              {[target.hotel, target.customer].filter(Boolean).join(' · ')}.{' '}
              {tr(target.closed ? 'pdf-import-target-closed' : 'pdf-import-target-review-text', { count: target.versionCount })}
            </Alert>
          )}
          {bookingConflict && job.status !== 'IMPORTED' && (
            <Alert
              severity={bookingConflict.deleted ? 'info' : 'error'}
              variant={bookingConflict.deleted ? 'outlined' : 'filled'}
              action={
                <Button
                  color="inherit"
                  variant={bookingConflict.deleted ? 'text' : 'outlined'}
                  sx={{ whiteSpace: 'nowrap' }}
                  component={Link}
                  to={`/offers/${bookingConflict.offerId}${bookingConflict.offerVersionId ? `/${bookingConflict.offerVersionId}` : ''}`}
                >
                  {tr('pdf-import-conflict-open')}
                </Button>
              }
            >
              {bookingConflict.deleted ? (
                <>
                  <AlertTitle>{tr('pdf-import-conflict-deleted-title', { refCode: bookingConflict.refCode })}</AlertTitle>
                  {tr('pdf-import-conflict-deleted-text', { refCode: bookingConflict.refCode, offerId: bookingConflict.offerId })}
                </>
              ) : (
                <>
                  <AlertTitle sx={{ fontWeight: 700 }}>{tr('pdf-import-conflict-title', { refCode: bookingConflict.refCode })}</AlertTitle>
                  {[bookingConflict.hotel, bookingConflict.customer].filter(Boolean).join(' · ')}. {tr('pdf-import-conflict-text')}
                </>
              )}
            </Alert>
          )}
          {/* Stack resets child margins, which would cancel the grid's negative spacing offset. */}
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={5}>
                <Box sx={{ position: { lg: 'sticky' }, top: 80 }}>
                  <PdfPreview key={job.id} importId={job.id} filename={job.filename} />
                  <Typography variant="caption">{tr('pdf-import-preview-hint')}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} lg={7}>
                {review && (
                  <Stack spacing={2}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack spacing={2} component="fieldset" disabled={busy || job.status !== 'REVIEW'} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                          <Typography variant="h6">
                            {tr(job.status === 'IMPORTED' ? 'pdf-import-imported-details' : 'pdf-import-check-details')}
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={
                              job.status !== 'IMPORTED' && bookingBlocked
                                ? 'error'
                                : job.status === 'IMPORTED' || !validation.length
                                  ? 'success'
                                  : 'warning'
                            }
                            label={
                              job.status !== 'IMPORTED' && bookingBlocked
                                ? tr('pdf-import-chip-blocked')
                                : job.status === 'IMPORTED'
                                  ? group.length
                                    ? tr('pdf-import-chip-version')
                                    : review.documentType === 'BOOKING_CONFIRMATION'
                                      ? tr('pdf-import-chip-reservation')
                                      : tr('pdf-import-chip-draft')
                                  : validation.length
                                    ? tr('pdf-import-open-points', { count: validation.length })
                                    : tr('pdf-import-chip-ready')
                            }
                          />
                        </Stack>
                        <Autocomplete
                          options={hotels}
                          disabled={!!target}
                          groupBy={hotel =>
                            tr(candidates.some(candidate => candidate.id === hotel.id) ? 'pdf-import-hotels-matching' : 'pdf-import-hotels-other')
                          }
                          loading={!hotelData}
                          loadingText={tr('pdf-import-hotels-loading')}
                          noOptionsText={tr('pdf-import-hotels-none')}
                          value={hotels.find(h => h.id === review.hotelId) || null}
                          getOptionLabel={option => `${option.name} (${option.businessCity})`}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          onInputChange={(_event, value, reason) => {
                            if (reason === 'input' || reason === 'clear') setHotelSearch(value);
                          }}
                          onOpen={() => setHotelSearch('')}
                          ListboxProps={{
                            onScroll: event => {
                              const list = event.currentTarget;
                              if (list.scrollTop + list.clientHeight >= list.scrollHeight - 40) void action(loadMoreHotels);
                            },
                          }}
                          onChange={(_event, hotel) => {
                            setHotelSearch('');
                            changeHotel(hotel);
                          }}
                          renderOption={(props, option) => {
                            const candidate = candidates.find(c => c.id === option.id);
                            return (
                              <li {...props} key={option.id}>
                                <Box>
                                  <Typography>
                                    {option.name} ({option.businessCity})
                                  </Typography>
                                  {candidate && (
                                    <Typography variant="caption" color={candidate.conflicts?.length ? 'error' : 'text.secondary'}>
                                      {tr(candidate.conflicts?.length ? 'pdf-import-hotel-differs' : 'pdf-import-hotel-matches')}
                                    </Typography>
                                  )}
                                </Box>
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label={tr('pdf-import-hotel-label')}
                              helperText={tr(target ? 'pdf-import-target-hotel' : 'pdf-import-hotel-helper')}
                            />
                          )}
                        />
                        {!!candidates.find(c => c.id === review.hotelId)?.conflicts?.length && (
                          <Alert severity="warning">
                            {tr('pdf-import-hotel-conflicts', {
                              conflicts: candidates.find(c => c.id === review.hotelId)!.conflicts!.join(', '),
                            })}
                          </Alert>
                        )}
                        {context && (
                          <Typography variant="body2" color="text.secondary">
                            {tr('pdf-import-prices-kept')}
                          </Typography>
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <TextField
                            fullWidth
                            select
                            label={tr('pdf-import-document-type')}
                            value={review.documentType}
                            onChange={e => edit(r => ({ ...r, documentType: e.target.value as PdfOfferImportReview['documentType'] }))}
                          >
                            <MenuItem value="UNKNOWN">{tr('pdf-import-document-type-unknown')}</MenuItem>
                            <MenuItem value="OFFER">{tr('pdf-import-document-type-offer')}</MenuItem>
                            <MenuItem value="BOOKING_CONFIRMATION">{tr('pdf-import-document-type-confirmation')}</MenuItem>
                          </TextField>
                          <TextField
                            fullWidth
                            label={tr('pdf-import-booking-number')}
                            placeholder={tr('pdf-import-booking-number-placeholder')}
                            disabled={!!target}
                            error={bookingBlocked}
                            value={review.bookingNumber}
                            inputProps={{ maxLength: 6, style: { textTransform: 'uppercase', letterSpacing: 1 } }}
                            helperText={
                              target
                                ? tr('pdf-import-target-number')
                                : bookingBlocked
                                  ? tr('pdf-import-booking-number-taken')
                                  : review.bookingNumber
                                    ? tr('pdf-import-booking-number-used')
                                    : tr('pdf-import-booking-number-empty')
                            }
                            onChange={e => edit(r => ({ ...r, bookingNumber: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') }))}
                          />
                        </Stack>
                      </Stack>
                    </Paper>
                    <Tabs
                      value={tab}
                      onChange={(_event, value: ReviewTab) => setTab(value)}
                      variant="scrollable"
                      scrollButtons={false}
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': { minWidth: 0, px: 1.25, textTransform: 'none', fontSize: 14 },
                      }}
                    >
                      {reviewTabs
                        .map(value => [value, sectionLabel(value)] as const)
                        .map(([value, label]) => (
                          <Tab
                            key={value}
                            value={value}
                            label={
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <span>{label}</span>
                                {value !== 'overview' && job.status === 'REVIEW' && (
                                  <Chip
                                    size="small"
                                    color={openBySection[value] ? 'warning' : 'success'}
                                    label={openBySection[value] || '✓'}
                                    sx={{ height: 20, minWidth: 24, '& .MuiChip-label': { px: 0.75 } }}
                                  />
                                )}
                              </Stack>
                            }
                          />
                        ))}
                    </Tabs>
                    <Stack spacing={2} component="fieldset" disabled={busy || job.status !== 'REVIEW'} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
                      {tab === 'overview' && (
                        <>
                          {group.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                              <Typography variant="subtitle1">{tr('pdf-import-group-title', { count: group.length })}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {tr('pdf-import-group-text')}
                              </Typography>
                              <Stack spacing={0.5} sx={{ mt: 1 }}>
                                {group.map((member, index) => (
                                  <Stack key={member.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="body2" sx={{ minWidth: 72 }}>
                                      {tr('pdf-import-version', { n: index + 1 })}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      color={member.documentType === 'BOOKING_CONFIRMATION' ? 'primary' : 'default'}
                                      label={member.documentType ? documentLabel(member.documentType) : tr('pdf-import-reading')}
                                    />
                                    {member.id === importId ? (
                                      <Typography variant="body2" fontWeight={600}>
                                        {member.filename} ({tr('pdf-import-opened')})
                                      </Typography>
                                    ) : (
                                      <Button size="small" component={Link} to={`/offers/pdf-import/${member.id}`} sx={{ textTransform: 'none' }}>
                                        {member.filename}
                                      </Button>
                                    )}
                                    <Typography
                                      variant="body2"
                                      color={member.status === 'REVIEW' && !member.openChecks ? 'success.main' : 'text.secondary'}
                                    >
                                      {statusLabel(member.status)}
                                      {member.status === 'REVIEW' && member.openChecks > 0
                                        ? ` · ${tr('pdf-import-group-open', { count: member.openChecks })}`
                                        : ''}
                                    </Typography>
                                  </Stack>
                                ))}
                              </Stack>
                              {job.status !== 'IMPORTED' && (
                                <Button
                                  size="small"
                                  sx={{ mt: 1 }}
                                  disabled={busy || dirty || job.status === 'IMPORTING'}
                                  title={tr('pdf-import-group-leave-hint')}
                                  onClick={() =>
                                    void action(async () => {
                                      await leaveGroup({ variables: { id: importId } });
                                      await refetch();
                                      await reloadJobs();
                                    })
                                  }
                                >
                                  {tr('pdf-import-group-leave')}
                                </Button>
                              )}
                            </Paper>
                          )}
                          {job.status === 'REVIEW' && validation.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'warning.light' }}>
                              <Typography variant="subtitle1">{tr('pdf-import-checks-title', { count: validation.length })}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {tr('pdf-import-checks-text')}
                              </Typography>
                              <Stack spacing={0.5} sx={{ mt: 1 }}>
                                {issues.map((issue, index) => {
                                  const section = issue.section;
                                  return (
                                    <Button
                                      key={index}
                                      size="small"
                                      color="inherit"
                                      onClick={() => setTab(section === 'header' ? 'overview' : section)}
                                      sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', fontWeight: 400 }}
                                      startIcon={
                                        <Chip size="small" variant="outlined" label={sectionLabel(section)} sx={{ height: 20, minWidth: 110 }} />
                                      }
                                    >
                                      {validation[index]}
                                    </Button>
                                  );
                                })}
                              </Stack>
                            </Paper>
                          )}
                          <Box>
                            <Grid container spacing={2}>
                              {(
                                [
                                  [
                                    'customer',
                                    [
                                      review.client.company ||
                                        [review.client.firstname, review.client.lastname].filter(Boolean).join(' ') ||
                                        tr('pdf-import-card-no-customer'),
                                      [[review.client.firstname, review.client.lastname].filter(Boolean).join(' '), review.client.email]
                                        .filter(Boolean)
                                        .join(' · '),
                                      [review.client.zip, review.client.city, review.client.country].filter(Boolean).join(' '),
                                    ],
                                  ],
                                  [
                                    'event',
                                    [
                                      review.event.title || tr('pdf-import-card-no-title'),
                                      review.event.startDate
                                        ? `${isoDate(review.event.startDate)}${review.event.endDate && review.event.endDate !== review.event.startDate ? ` – ${isoDate(review.event.endDate)}` : ''}`
                                        : tr('pdf-import-card-no-period'),
                                      review.event.participants !== null
                                        ? tr('pdf-import-card-participants', { count: review.event.participants })
                                        : tr('pdf-import-card-participants-unknown'),
                                    ],
                                  ],
                                  [
                                    'services',
                                    [
                                      tr('pdf-import-card-services', { count: review.lineItems.length }),
                                      review.totalGross !== null
                                        ? tr('pdf-import-card-gross', { amount: formatPrice(review.totalGross) })
                                        : tr('pdf-import-card-no-total'),
                                      totalDifference ? tr('pdf-import-card-printed', { amount: formatPrice(totalDifference.printed) }) : '',
                                    ],
                                  ],
                                  [
                                    'terms',
                                    [
                                      review.cancellationRules.length
                                        ? tr('pdf-import-card-tiers', { count: review.cancellationRules.length })
                                        : tr('pdf-import-card-no-tiers'),
                                      review.depositAmount !== null
                                        ? tr('pdf-import-card-deposit', { amount: formatPrice(review.depositAmount) })
                                        : tr('pdf-import-card-no-deposit'),
                                      review.validUntil ? tr('pdf-import-card-valid-until', { date: isoDate(review.validUntil) }) : '',
                                    ],
                                  ],
                                ] as [Exclude<ReviewTab, 'overview'>, string[]][]
                              ).map(([section, lines]) => (
                                <Grid item xs={12} sm={6} key={section}>
                                  <Paper
                                    variant="outlined"
                                    onClick={() => setTab(section)}
                                    sx={{ p: 2, borderRadius: 2, height: '100%', cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                                  >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                      <Typography variant="overline" color="text.secondary">
                                        {sectionLabel(section)}
                                      </Typography>
                                      {job.status === 'REVIEW' && openBySection[section] > 0 && (
                                        <Chip size="small" color="warning" variant="outlined" label={`${openBySection[section]} offen`} />
                                      )}
                                    </Stack>
                                    {lines.filter(Boolean).map((line, index) => (
                                      <Typography
                                        key={index}
                                        variant={index ? 'body2' : 'subtitle1'}
                                        color={index ? 'text.secondary' : 'text.primary'}
                                      >
                                        {line}
                                      </Typography>
                                    ))}
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </>
                      )}
                      {tab === 'customer' && (
                        <Stack spacing={2} component="fieldset" disabled={!!target} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
                          {target && <Alert severity="info">{tr('pdf-import-target-customer')}</Alert>}
                          <LabeledFrame variant="h6" label={tr('pdf-import-section-customer')}>
                            <Stack useFlexGap spacing={2}>
                              {job.status === 'REVIEW' && (
                                <Autocomplete
                                  options={clients}
                                  value={null}
                                  inputValue={clientSearch}
                                  filterOptions={options => options}
                                  getOptionLabel={client =>
                                    [client.company, `${client.firstname} ${client.lastname}`.trim(), client.email].filter(Boolean).join(' · ')
                                  }
                                  noOptionsText={tr('pdf-import-customers-none')}
                                  onInputChange={(_event, value, reason) => {
                                    if (reason === 'input' || reason === 'clear') setClientSearch(value);
                                  }}
                                  onChange={(_event, client) => {
                                    setClientSearch('');
                                    applyClient(client);
                                  }}
                                  renderInput={params => (
                                    <TextField
                                      {...params}
                                      label={tr('pdf-import-customer-existing')}
                                      helperText={tr('pdf-import-customer-existing-helper')}
                                    />
                                  )}
                                />
                              )}
                              <Autocomplete
                                options={companies}
                                value={companies.find(c => c.id === review.publicCompanyId) || null}
                                inputValue={companySearch}
                                getOptionLabel={c => c.name}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                onInputChange={(_event, value, reason) => {
                                  if (reason === 'input') setCompanySearch(value);
                                }}
                                onChange={(_event, value) => {
                                  setCompanySearch(value?.name || '');
                                  edit(r => ({ ...r, publicCompanyId: value?.id || null }));
                                }}
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    label={tr('pdf-import-customer-company')}
                                    helperText={tr('pdf-import-customer-company-helper')}
                                  />
                                )}
                              />
                              <Grid container spacing={1}>
                                {(['company', 'firstname', 'lastname', 'email', 'phone', 'address', 'zip', 'city', 'country'] as const)
                                  .map(key => [key, tr(`pdf-import-client-${key}`)] as const)
                                  .map(([key, label]) => (
                                    <Grid item xs={12} sm={6} key={key}>
                                      {key === 'country' ? (
                                        <PdfImportCountry
                                          label={label}
                                          value={review.client.country}
                                          required
                                          onChange={country => edit(r => ({ ...r, client: { ...r.client, country } }))}
                                        />
                                      ) : (
                                        <TextField
                                          fullWidth
                                          required={key === 'email'}
                                          label={label}
                                          placeholder={tr('pdf-import-unknown')}
                                          helperText={!review.client[key] ? tr('pdf-import-unknown') : undefined}
                                          value={review.client[key]}
                                          onChange={e =>
                                            edit(r => ({
                                              ...r,
                                              client: { ...r.client, [key]: e.target.value },
                                            }))
                                          }
                                        />
                                      )}
                                    </Grid>
                                  ))}
                              </Grid>
                            </Stack>
                          </LabeledFrame>
                          <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box>
                                <Typography>{tr('pdf-import-billing')}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {review.request?.billing.company || tr('pdf-import-billing-hint')}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <PdfImportBilling
                                request={review.request || emptyPdfImportRequest()}
                                onChange={request => edit(r => ({ ...r, request }))}
                              />
                            </AccordionDetails>
                          </Accordion>
                        </Stack>
                      )}
                      {tab === 'event' && (
                        <>
                          <LabeledFrame variant="h6" label={tr('pdf-import-section-event')}>
                            <Stack useFlexGap spacing={1}>
                              <TextField
                                label={tr('pdf-import-event-title')}
                                value={review.event.title}
                                onChange={e => edit(r => ({ ...r, event: { ...r.event, title: e.target.value } }))}
                              />
                              <Grid container spacing={1}>
                                {(['startDate', 'endDate'] as const).map(key => (
                                  <Grid item xs={6} key={key}>
                                    <TextField
                                      fullWidth
                                      type="date"
                                      label={tr(key === 'startDate' ? 'pdf-import-room-from' : 'pdf-import-room-to')}
                                      InputLabelProps={{ shrink: true }}
                                      value={review.event[key]}
                                      onChange={e =>
                                        edit(r => {
                                          const next = { ...r, event: { ...r.event, [key]: e.target.value } };
                                          return {
                                            ...next,
                                            request: { ...(r.request || emptyPdfImportRequest()), days: pdfImportReviewRequestDays(next) },
                                          };
                                        })
                                      }
                                    />
                                  </Grid>
                                ))}
                                <Grid item xs={6}>
                                  <TextField
                                    label={tr('pdf-import-room-participants')}
                                    placeholder={tr('pdf-import-unknown')}
                                    helperText={review.event.participants === null ? tr('pdf-import-unknown') : undefined}
                                    type="number"
                                    value={review.event.participants ?? ''}
                                    onChange={e => edit(r => ({ ...r, event: { ...r.event, participants: number(e.target.value) } }))}
                                  />
                                </Grid>
                              </Grid>
                            </Stack>
                          </LabeledFrame>
                          <Divider />
                          <LabeledFrame variant="h6" label={tr('pdf-import-event-requirements')}>
                            <PdfImportRequestDetails
                              startDate={review.event.startDate}
                              endDate={review.event.endDate}
                              request={{ ...(review.request || emptyPdfImportRequest()), days: pdfImportReviewRequestDays(review) }}
                              onChange={request => edit(r => ({ ...r, request }))}
                            />
                          </LabeledFrame>
                        </>
                      )}
                      {tab === 'services' && (
                        <>
                          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <Typography>{tr('pdf-import-prices-are')}</Typography>
                            <ToggleButtonGroup
                              exclusive
                              size="small"
                              aria-label={tr('pdf-import-price-basis-all')}
                              value={
                                review.lineItems.every(line => line.priceBasis === 'NET')
                                  ? 'NET'
                                  : review.lineItems.every(line => line.priceBasis === 'GROSS')
                                    ? 'GROSS'
                                    : ''
                              }
                              onChange={(_event, basis: 'NET' | 'GROSS' | null) => {
                                if (basis) edit(current => changePdfImportPriceBasis(current, basis));
                              }}
                            >
                              <ToggleButton value="NET">{tr('pdf-import-net')}</ToggleButton>
                              <ToggleButton value="GROSS">{tr('pdf-import-gross')}</ToggleButton>
                            </ToggleButtonGroup>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {tr('pdf-import-price-basis-hint')}
                          </Typography>
                          <PdfImportEditor
                            review={review}
                            context={context}
                            disabled={busy || job.status !== 'REVIEW'}
                            onChange={next => {
                              setReview(next);
                              setDirty(true);
                            }}
                            onTaxChange={changeTax}
                            onSplitChange={changeSplit}
                            onMixedChange={changeMixed}
                          />
                          {visibleTaxHints.some(hint => hint.status === 'REVIEW' && !review.lineItems[hint.lineIndex]?.taxRateId) && (
                            <Alert severity="info">{tr('pdf-import-tax-unclear')}</Alert>
                          )}
                          {context && (
                            <Button size="small" sx={{ alignSelf: 'flex-start' }} disabled={busy} onClick={() => action(applyMappings)}>
                              {tr('pdf-import-tax-recheck')}
                            </Button>
                          )}
                          <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>{tr('pdf-import-lines-details')}</AccordionSummary>
                            <AccordionDetails>
                              <Stack spacing={2}>
                                {review.lineItems.map((line, index) => (
                                  <Stack useFlexGap key={index} spacing={1} sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1 }}>
                                    <Typography>{tr('pdf-import-item', { n: index + 1 })}</Typography>
                                    <TextField
                                      label={tr('pdf-import-line-description')}
                                      multiline
                                      value={line.description}
                                      onChange={e => editLine(index, { description: e.target.value })}
                                    />
                                    <Grid container spacing={1}>
                                      <Grid item xs={6}>
                                        <TextField
                                          fullWidth
                                          type="date"
                                          InputLabelProps={{ shrink: true }}
                                          label={tr('pdf-import-line-date')}
                                          value={line.date}
                                          onChange={e => editLine(index, { date: e.target.value })}
                                        />
                                      </Grid>
                                      <Grid item xs={3}>
                                        <TextField
                                          fullWidth
                                          label={tr('pdf-import-quantity')}
                                          type="number"
                                          value={line.quantity ?? ''}
                                          onChange={e => editLine(index, { quantity: number(e.target.value) })}
                                        />
                                      </Grid>
                                      <Grid item xs={3}>
                                        <TextField
                                          fullWidth
                                          label={tr('pdf-import-line-unit')}
                                          value={line.unit}
                                          onChange={e => editLine(index, { unit: e.target.value })}
                                        />
                                      </Grid>
                                      {(['unitPrice', 'totalNet', 'totalGross', 'taxAmount'] as const)
                                        .map(key => [key, tr(`pdf-import-line-${key}`)] as const)
                                        .map(([key, label]) => (
                                          <Grid item xs={6} key={key}>
                                            <TextField
                                              fullWidth
                                              label={label}
                                              type="number"
                                              inputProps={{ step: '0.01' }}
                                              value={line[key] ?? ''}
                                              onChange={e => editLine(index, { [key]: number(e.target.value) })}
                                            />
                                          </Grid>
                                        ))}
                                      <Grid item xs={6}>
                                        <Typography variant="caption">{tr('pdf-import-unit-price-is')}</Typography>
                                        <ToggleButtonGroup
                                          exclusive
                                          fullWidth
                                          size="small"
                                          aria-label={tr('pdf-import-price-basis-line', { n: index + 1 })}
                                          value={line.priceBasis}
                                          onChange={(_event, basis: 'NET' | 'GROSS' | null) => {
                                            if (basis) edit(current => changePdfImportPriceBasis(current, basis, index));
                                          }}
                                        >
                                          <ToggleButton value="NET">{tr('pdf-import-net')}</ToggleButton>
                                          <ToggleButton value="GROSS">{tr('pdf-import-gross')}</ToggleButton>
                                        </ToggleButtonGroup>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField
                                          fullWidth
                                          select
                                          label={tr('pdf-import-line-tax')}
                                          value={line.taxShares.length > 1 ? 'mixed' : line.taxRateId || ''}
                                          onChange={e => {
                                            const rate = context?.taxRates.find(t => t.id === Number(e.target.value));
                                            changeTax(index, rate?.id || null);
                                          }}
                                        >
                                          <MenuItem value="">{tr('pdf-import-please-choose')}</MenuItem>
                                          {line.taxShares.length > 1 && (
                                            <MenuItem value="mixed" disabled>
                                              {tr('pdf-import-mixed-catalog')}
                                            </MenuItem>
                                          )}
                                          {context?.taxRates.map(t => (
                                            <MenuItem key={t.id} value={t.id}>
                                              {t.name} — {(t.rate * 100).toFixed(2)} %
                                            </MenuItem>
                                          ))}
                                        </TextField>
                                      </Grid>
                                    </Grid>
                                    <TextField
                                      select
                                      label={tr('pdf-import-line-category')}
                                      helperText={tr('pdf-import-line-category-helper')}
                                      value={line.serviceCategory}
                                      onChange={e => editLine(index, { serviceCategory: e.target.value })}
                                    >
                                      <MenuItem value="">{tr('pdf-import-not-assigned')}</MenuItem>
                                      {Object.keys(pdfImportServiceCategories).map(code => (
                                        <MenuItem key={code} value={code}>
                                          {tr(`pdf-import-category-${code}`)}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                    <TextField
                                      label={tr('pdf-import-line-notes')}
                                      multiline
                                      value={line.notes}
                                      onChange={e => editLine(index, { notes: e.target.value })}
                                    />
                                    <Button
                                      color="error"
                                      onClick={() => edit(r => ({ ...r, lineItems: r.lineItems.filter((_line, i) => i !== index) }))}
                                    >
                                      {tr('pdf-import-line-remove')}
                                    </Button>
                                  </Stack>
                                ))}
                                <Button
                                  onClick={() =>
                                    edit(r => ({
                                      ...r,
                                      lineItems: [
                                        ...r.lineItems,
                                        {
                                          date: r.event.startDate,
                                          description: '',
                                          quantity: 1,
                                          unit: '',
                                          unitPrice: null,
                                          priceBasis: 'GROSS',
                                          totalNet: null,
                                          totalGross: null,
                                          taxRateId: null,
                                          taxPercent: null,
                                          taxAmount: null,
                                          taxShares: [],
                                          serviceCategory: '',
                                          notes: '',
                                        },
                                      ],
                                    }))
                                  }
                                >
                                  {tr('pdf-import-line-add')}
                                </Button>
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                          <LabeledFrame variant="h6" label={tr('pdf-import-totals')}>
                            <Grid container spacing={1}>
                              {(['totalNet', 'totalGross', 'totalTax'] as const)
                                .map(key => [key, tr(`pdf-import-sum-${key}`)] as const)
                                .map(([key, label]) => (
                                  <Grid item xs={6} key={key}>
                                    <TextField
                                      fullWidth
                                      label={label}
                                      type="number"
                                      inputProps={{ step: '0.01' }}
                                      value={review[key] ?? ''}
                                      onChange={e => edit(r => ({ ...r, [key]: number(e.target.value) }))}
                                    />
                                  </Grid>
                                ))}
                            </Grid>
                          </LabeledFrame>
                        </>
                      )}
                      {tab === 'terms' && (
                        <>
                          <LabeledFrame variant="h6" label={tr('pdf-import-col-document')}>
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  label={tr('pdf-import-external-reference')}
                                  value={review.externalReference}
                                  onChange={e => edit(r => ({ ...r, externalReference: e.target.value }))}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  label={tr('pdf-import-currency')}
                                  value={review.currency}
                                  onChange={e => edit(r => ({ ...r, currency: e.target.value.toUpperCase() }))}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  select
                                  label={tr('pdf-import-offer-language')}
                                  value={review.language}
                                  onChange={e => edit(r => ({ ...r, language: e.target.value }))}
                                >
                                  <MenuItem value="de">{tr('pdf-import-language-de')}</MenuItem>
                                  <MenuItem value="en">{tr('pdf-import-language-en')}</MenuItem>
                                </TextField>
                              </Grid>
                              {(['documentDate', 'validUntil'] as const)
                                .map(key => [key, tr(`pdf-import-${key}`)] as const)
                                .map(([key, label]) => (
                                  <Grid item xs={6} key={key}>
                                    <TextField
                                      fullWidth
                                      type="date"
                                      label={label}
                                      InputLabelProps={{ shrink: true }}
                                      value={review[key]}
                                      onChange={e => edit(r => ({ ...r, [key]: e.target.value }))}
                                    />
                                  </Grid>
                                ))}
                            </Grid>
                          </LabeledFrame>
                          <LabeledFrame variant="h6" label={tr('pdf-import-terms-title')}>
                            <Stack useFlexGap spacing={2}>
                              <Grid container spacing={1}>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    label={tr('pdf-import-deposit')}
                                    type="number"
                                    inputProps={{ step: '0.01' }}
                                    value={review.depositAmount ?? ''}
                                    onChange={e => edit(r => ({ ...r, depositAmount: number(e.target.value) }))}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    label={tr('pdf-import-deposit-due')}
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={review.depositDue}
                                    onChange={e => edit(r => ({ ...r, depositDue: e.target.value }))}
                                  />
                                </Grid>
                              </Grid>
                              <Stack useFlexGap spacing={1}>
                                <Typography variant="subtitle2">{tr('pdf-import-tiers')}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {tr('pdf-import-tiers-hint')}
                                </Typography>
                                {review.cancellationRules.map((rule, index) => (
                                  <Grid container spacing={1} key={index} alignItems="center">
                                    <Grid item xs={6} sm={3}>
                                      <TextField
                                        fullWidth
                                        type="number"
                                        label={tr('pdf-import-tier-days')}
                                        value={rule.daysToEvent ?? ''}
                                        onChange={e =>
                                          edit(r => ({
                                            ...r,
                                            cancellationRules: r.cancellationRules.map((item, i) =>
                                              i === index ? { ...item, daysToEvent: number(e.target.value) } : item,
                                            ),
                                          }))
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                      <TextField
                                        fullWidth
                                        type="number"
                                        label={tr('pdf-import-tier-percent')}
                                        inputProps={{ step: '0.01' }}
                                        value={rule.percent ?? ''}
                                        onChange={e =>
                                          edit(r => ({
                                            ...r,
                                            cancellationRules: r.cancellationRules.map((item, i) =>
                                              i === index ? { ...item, percent: number(e.target.value) } : item,
                                            ),
                                          }))
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={10} sm={6}>
                                      <TextField
                                        fullWidth
                                        label={tr('pdf-import-tier-wording')}
                                        helperText={pdfImportCancellationRuleText(rule, language)}
                                        value={rule.description}
                                        onChange={e =>
                                          edit(r => ({
                                            ...r,
                                            cancellationRules: r.cancellationRules.map((item, i) =>
                                              i === index ? { ...item, description: e.target.value } : item,
                                            ),
                                          }))
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={2} sm={1}>
                                      <Button
                                        color="error"
                                        onClick={() =>
                                          edit(r => ({ ...r, cancellationRules: r.cancellationRules.filter((_rule, i) => i !== index) }))
                                        }
                                      >
                                        {tr('pdf-import-remove')}
                                      </Button>
                                    </Grid>
                                  </Grid>
                                ))}
                                <Button
                                  sx={{ alignSelf: 'flex-start' }}
                                  onClick={() => edit(r => ({ ...r, cancellationRules: [...r.cancellationRules, emptyPdfImportCancellationRule()] }))}
                                >
                                  {tr('pdf-import-tier-add')}
                                </Button>
                              </Stack>
                              {(['cancellationTerms', 'paymentTerms', 'additionalTerms'] as const)
                                .map(key => [key, tr(`pdf-import-${key}`)] as const)
                                .map(([key, label]) => (
                                  <TextField
                                    key={key}
                                    multiline
                                    minRows={2}
                                    label={label}
                                    value={review[key]}
                                    onChange={e => edit(r => ({ ...r, [key]: e.target.value }))}
                                  />
                                ))}
                            </Stack>
                          </LabeledFrame>
                        </>
                      )}
                    </Stack>
                    {differingDocuments.length > 0 && job.status === 'REVIEW' && (
                      <Alert severity="warning">
                        {totalDifference && (
                          <Typography variant="body2">
                            {tr('pdf-import-difference-text', {
                              offer: totalDifference.offer.toLocaleString('de-AT', { minimumFractionDigits: 2 }),
                              printed: totalDifference.printed.toLocaleString('de-AT', { minimumFractionDigits: 2 }),
                            })}
                          </Typography>
                        )}
                        {differingDocuments.length > (totalDifference ? 1 : 0) && (
                          <Typography variant="body2">
                            {tr('pdf-import-difference-others', { files: differingDocuments.slice(totalDifference ? 1 : 0).join(', ') })}
                          </Typography>
                        )}
                        <FormControlLabel
                          control={
                            <Checkbox checked={totalDifferenceConfirmed} onChange={event => setTotalDifferenceConfirmed(event.target.checked)} />
                          }
                          label={tr('pdf-import-difference-confirm')}
                        />
                      </Alert>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {target
                        ? tr(importReservation ? 'pdf-import-info-version-reservation' : 'pdf-import-info-version', {
                            refCode: target.refCode,
                            count: Math.max(group.length, 1),
                          })
                        : group.length
                          ? tr(importReservation ? 'pdf-import-info-booking-reservation' : 'pdf-import-info-booking', { count: group.length })
                          : review.documentType === 'BOOKING_CONFIRMATION'
                            ? tr('pdf-import-info-reservation')
                            : tr('pdf-import-info-offer')}
                    </Typography>
                    {job.status === 'REVIEW' && (
                      <Paper
                        variant="outlined"
                        sx={{ position: 'sticky', bottom: 12, zIndex: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 2 }}
                      >
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                          <Button
                            variant="outlined"
                            disabled={!dirty || busy}
                            onClick={() =>
                              void action(async () => {
                                await saveReview();
                              })
                            }
                          >
                            {tr('pdf-import-save')}
                          </Button>
                          <Button
                            variant="contained"
                            disabled={
                              validation.length > 0 ||
                              !!target?.closed ||
                              busy ||
                              job.status !== 'REVIEW' ||
                              !!groupBlocker ||
                              bookingBlocked ||
                              (differingDocuments.length > 0 && !totalDifferenceConfirmed)
                            }
                            onClick={() =>
                              void action(async () => {
                                const confirmedRevision = dirty ? await saveReview() : revision;
                                const result = await importOffer({
                                  variables: { id: importId, revision: confirmedRevision, confirmTotalDifference: totalDifferenceConfirmed },
                                });
                                if (result.data) {
                                  setDirty(false);
                                  await refetch();
                                  await reloadJobs();
                                }
                              })
                            }
                          >
                            {busy
                              ? tr('pdf-import-busy')
                              : target
                                ? tr(
                                    group.length
                                      ? 'pdf-import-create-versions'
                                      : importReservation
                                        ? 'pdf-import-create-version-reservation'
                                        : 'pdf-import-create-version',
                                    {
                                      count: group.length,
                                    },
                                  )
                                : group.length
                                  ? tr('pdf-import-create-booking', { count: group.length })
                                  : review.documentType === 'BOOKING_CONFIRMATION'
                                    ? tr('pdf-import-create-reservation')
                                    : tr('pdf-import-create-offer')}
                          </Button>
                          {validation.length > 0 && (
                            <Button
                              color="warning"
                              onClick={() => {
                                const section = issues[0].section;
                                setTab(section === 'header' ? 'overview' : section);
                              }}
                            >
                              {tr('pdf-import-open-points-show', { count: validation.length })}
                            </Button>
                          )}
                          {bookingBlocked && (
                            <Typography variant="body2" color="error" fontWeight={600}>
                              {tr('pdf-import-booking-number-taken-bar', { refCode: review.bookingNumber })}
                            </Typography>
                          )}
                          {groupBlocker && (
                            <Typography variant="body2" color="text.secondary">
                              {groupBlocker}
                            </Typography>
                          )}
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                )}
                {(review || job.rawJson) && (
                  <Accordion sx={{ mt: 3 }} disableGutters elevation={0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2" color="text.secondary">
                        {tr('pdf-import-technical')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        {(job.error || error || queryError || contextError) && (
                          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                            {job.error || error || queryError?.message || contextError?.message}
                          </Typography>
                        )}
                        {review && job.status === 'REVIEW' && (
                          <Box>
                            <Button
                              disabled={dirty || busy || job.status !== 'REVIEW'}
                              title={tr('pdf-import-reread-hint')}
                              onClick={() =>
                                void action(async () => {
                                  await retry({ variables: { id: importId } });
                                  await refetch();
                                })
                              }
                            >
                              {tr('pdf-import-reread')}
                            </Button>
                          </Box>
                        )}
                        {preparation && (
                          <Box>
                            <Typography variant="subtitle2">{tr('pdf-import-recognition-notes')}</Typography>
                            <Box component="ul" sx={{ pl: 2.5 }}>
                              {[...preparation.explanations, ...preparation.issues.map(issue => issue.reason)].map((message, index) => (
                                <li key={index}>{message}</li>
                              ))}
                            </Box>
                          </Box>
                        )}
                        {review && (
                          <Button onClick={() => download('seminargo-import-review.json', review)}>{tr('pdf-import-download-review')}</Button>
                        )}
                        {job.rawJson && (
                          <>
                            <Button onClick={() => download('bda-extraction.json', json(job.rawJson, {}))}>{tr('pdf-import-download-raw')}</Button>
                            <Button onClick={() => setShowRaw(!showRaw)}>{tr(showRaw ? 'pdf-import-raw-hide' : 'pdf-import-raw-show')}</Button>
                            {showRaw && (
                              <Box component="pre" sx={{ overflow: 'auto', maxHeight: 400, whiteSpace: 'pre-wrap', fontSize: 11 }}>
                                {JSON.stringify(json(job.rawJson, {}), null, 2)}
                              </Box>
                            )}
                          </>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Stack>
  );
}
